import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiHeart, FiMinus, FiPlus, FiTruck } from "react-icons/fi";
import productData from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, wishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.size[0] || "");
  const [selectedColor, setSelectedColor] = useState(product?.colors[0] || "");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const recommendations = useMemo(() => {
    if (!product) return [];
    return productData
      .filter((item) => item.id !== product.id && item.category === product.category)
      .slice(0, 3);
  }, [product]);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size[0]);
    setSelectedColor(product.colors[0]);
    setSelectedImage(0);
    setQuantity(1);
  }, [product]);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (!product) {
    return (
      <main className="product-not-found page-shell">
        <p className="eyebrow">404 / Product</p>
        <h1>Deze broek is niet gevonden.</h1>
        <Link className="solid-button" to="/shop" onClick={scrollTop}>
          Terug naar collectie
        </Link>
      </main>
    );
  }

  const saved = wishlist.includes(product.id);
  const shippingDate = new Intl.DateTimeFormat("nl-BE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(Date.now() + 4 * 24 * 60 * 60 * 1000));

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };

  const buyNow = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/cartpage");
    scrollTop();
  };

  return (
    <main className="product-detail app-page-enter">
      <div className="product-detail__breadcrumb">
        <Link to="/shop" onClick={scrollTop}>
          <FiArrowLeft aria-hidden="true" /> Collectie
        </Link>
        <span>{product.category}</span>
        <span>{product.sku}</span>
      </div>

      <div className="product-detail__layout">
        <section className="product-gallery" aria-label={`${product.name} images`}>
          <div className="product-gallery__main">
            <img
              src={product.images[selectedImage]}
              alt={product.name}
              onError={(event) => {
                event.currentTarget.src = "/main/ss26-hero.jpg";
              }}
            />
            <span>{String(selectedImage + 1).padStart(2, "0")} / {String(product.images.length).padStart(2, "0")}</span>
          </div>
          {product.images.length > 1 && (
            <div className="product-gallery__thumbs">
              {product.images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={selectedImage === index ? "is-active" : ""}
                  onClick={() => setSelectedImage(index)}
                  aria-label={`Show image ${index + 1}`}
                >
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          )}
        </section>

        <aside className="product-panel">
          <div className="product-panel__topline">
            <span>{product.category} / {product.attributes.fit} fit</span>
            <button
              type="button"
              className={saved ? "is-saved" : ""}
              onClick={() => toggleWishlist(product.id)}
              aria-label={`${saved ? "Remove from" : "Add to"} wishlist`}
            >
              <FiHeart aria-hidden="true" />
            </button>
          </div>
          <h1>{product.name}</h1>
          <div className="product-panel__price">{formatPrice(product.price)}</div>
          <p className="product-panel__tax">Inclusief btw. Verzending berekend bij checkout.</p>
          <p className="product-panel__description">{product.description}</p>

          <div className="product-panel__option">
            <div>
              <label htmlFor="product-colour">Kleur</label>
              <span>{selectedColor}</span>
            </div>
            <select
              id="product-colour"
              value={selectedColor}
              onChange={(event) => setSelectedColor(event.target.value)}
            >
              {product.colors.map((color) => (
                <option key={color} value={color}>{color}</option>
              ))}
            </select>
          </div>

          <div className="product-panel__option">
            <div>
              <label htmlFor="product-size">Maat</label>
              <button type="button" onClick={() => window.alert("Meet je taille op het smalste punt en kies je gebruikelijke binnenbeenlengte.")}>Maattabel</button>
            </div>
            <select
              id="product-size"
              value={selectedSize}
              onChange={(event) => setSelectedSize(event.target.value)}
            >
              {product.size.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="product-panel__quantity">
            <span>Aantal</span>
            <div>
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity">
                <FiMinus aria-hidden="true" />
              </button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((value) => Math.min(6, value + 1))} aria-label="Increase quantity">
                <FiPlus aria-hidden="true" />
              </button>
            </div>
          </div>

          <button className="product-panel__add" type="button" onClick={handleAddToCart}>
            {added ? "Toegevoegd aan shopping bag" : `In shopping bag — ${formatPrice(product.price * quantity)}`}
          </button>
          <button className="product-panel__buy" type="button" onClick={buyNow}>Nu kopen</button>

          <div className="product-panel__delivery">
            <FiTruck aria-hidden="true" />
            <p>
              <strong>Verwachte levering {shippingDate}</strong>
              Gratis vanaf {formatPrice(siteConfig.freeShippingThreshold)}. Retour binnen 30 dagen.
            </p>
          </div>

          <div className="product-accordions">
            <details open>
              <summary>Fit & details</summary>
              <dl>
                <div><dt>Fit</dt><dd>{product.attributes.fit}</dd></div>
                <div><dt>Rise</dt><dd>{product.attributes.rise}</dd></div>
                <div><dt>Leg</dt><dd>{product.attributes.leg}</dd></div>
                <div><dt>Style code</dt><dd>{product.sku}</dd></div>
              </dl>
            </details>
            <details>
              <summary>Materials & details</summary>
              <p>{product.attributes.composition}</p>
              <p>Fabric {product.attributes.fabricCode}. {product.attributes.origin}.</p>
            </details>
            <details>
              <summary>Care</summary>
              <p>{product.attributes.care}. Was alleen wanneer nodig en laat aan de lucht drogen.</p>
            </details>
          </div>
        </aside>
      </div>

      <section className="product-traceability">
        <p className="eyebrow">Product details / fabric {product.attributes.fabricCode}</p>
        <h2>Get to know the fit, fabric and care of your trousers.</h2>
        <div>
          <article><span>01</span><h3>Fit</h3><p>{product.fit} fit. {product.leg}.</p></article>
          <article><span>02</span><h3>Fabric</h3><p>{product.composition}.</p></article>
          <article><span>03</span><h3>Care</h3><p>{product.attributes.care}. Refer to the garment label.</p></article>
        </div>
      </section>

      {recommendations.length > 0 && (
        <section className="product-recommendations">
          <div>
            <p className="eyebrow">You may also like</p>
            <h2>Meer in {product.category}</h2>
          </div>
          <div className="product-recommendations__grid">
            {recommendations.map((item) => (
              <Link key={item.id} to={`/shopDetails/${item.id}`} onClick={scrollTop}>
                <img src={item.image} alt={item.name} loading="lazy" />
                <span>{item.attributes.fit} fit / {item.color}</span>
                <h3>{item.name}</h3>
                <strong>{formatPrice(item.price)}</strong>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
